/* The console's screens, grouped the way the sidebar shows them.
 *
 * Groups follow what an operator came to do, commonest first: watch this edge,
 * weigh what it gains and costs, prove it, change how traffic is carried,
 * secure it, fix it, experiment. Fleet screens sit above them and
 * administration below. Advanced holds the screens few visits need, the live
 * readouts and specialist tools, so they do not crowd the everyday groups.
 * Keys are addresses (deep links, the tour, Ada's proposals) and do not
 * change; labels and groups can.
 *
 * Every gate is declared here once. `modes` limits a screen to the edge modes
 * it works in, `phone` marks what a phone answers (it serves a read-only part
 * of the edge API, so with a phone selected every other screen is left out,
 * the way a direct-mode screen is left out for an anchored edge), `phoneOnly`
 * keeps a screen to a phone or a laptop, the devices that report as one, `hideOnSingle`
 * keeps a screen or a whole group off the on-device console, `minRole` reserves
 * a group or a screen for a portal role (a courtesy: the server enforces
 * access), and `defaultOpen` opens a group until the operator closes it.
 *
 * Ada names screens by these groups and labels from her own list in
 * cloud-anchor/pkg/nabxagent/prompt.go, and a Go test fails when the two differ.
 */
import {
  FiActivity, FiAlertTriangle, FiBarChart2, FiBatteryCharging, FiBell, FiCheckCircle, FiCheckSquare, FiClock, FiCloud,
  FiCpu, FiCrosshair, FiDatabase, FiDownloadCloud, FiEdit3, FiEyeOff, FiFileText, FiGitBranch, FiGrid, FiKey, FiLayers,
  FiLink, FiList, FiLock, FiMapPin, FiMonitor, FiPieChart, FiRadio, FiSearch, FiShare2, FiShield, FiShuffle, FiSliders,
  FiTerminal, FiTrendingUp, FiUserCheck, FiUsers, FiWifi, FiWind, FiZap,
} from "react-icons/fi";

export const NAV_CATEGORIES = [
  {
    id: "fleet",
    label: "Fleet Management",
    hideOnSingle: true,
    phone: true,
    items: [
      { key: "nodes", label: "Fleet Overview", icon: FiLayers },
      { key: "remote-devices", label: "Remote Devices", icon: FiLink },
      { key: "pilot-insights", label: "Pilot Insights", icon: FiTrendingUp },
      // Each home's Wi-Fi as its box judges it, in counts.
      { key: "home-wifi-fleet", label: "Wi-Fi Across Homes", icon: FiWifi },
      // The Agent Mesh's investigations: reads of a site from both ends, signed.
      { key: "investigations", label: "Investigations", icon: FiSearch },
      // One setting changed at a customer's sites, a site at a time, each tried by its box.
      { key: "changes", label: "Changes", icon: FiEdit3 },
      // Who may change the customer's boxes, and the signed record of what they did.
      { key: "trust-fabric", label: "Trust Fabric", icon: FiUserCheck },
    ],
  },
  {
    // What every visit checks: is it working, and did anything break.
    id: "monitoring",
    label: "Monitoring",
    defaultOpen: true,
    items: [
      { key: "dashboard", label: "Dashboard", icon: FiGrid, phone: true },
      { key: "connection-history", label: "Connection History", icon: FiWifi, phone: true, phoneOnly: true },
      { key: "continuity", label: "Connection Interruptions", icon: FiAlertTriangle, modes: ["anchored"] },
      // Each device on the home network as the person using it would put it.
      { key: "home-wifi", label: "Home Wi-Fi", icon: FiWifi },
      { key: "health", label: "WAN Health", icon: FiActivity, phone: true },
    ],
  },
  {
    id: "performance",
    label: "Performance & Cost",
    items: [
      { key: "bonding", label: "Bonding Gain", icon: FiShuffle, modes: ["anchored"] },
      { key: "data-budgets", label: "Data Budgets", icon: FiPieChart, modes: ["anchored"] },
    ],
  },
  {
    // The suites that check what this edge claims, run on the edge itself. A
    // section of their own: they are what an operator shows someone, not what
    // they reach for when something breaks.
    id: "verification",
    label: "Verification",
    items: [{ key: "verify", label: "Test Suites", icon: FiCheckSquare, modes: ["anchored"] }],
  },
  {
    id: "traffic-settings",
    label: "Traffic Settings",
    items: [
      { key: "traffic", label: "Traffic Shaping", icon: FiSliders, modes: ["anchored"] },
      { key: "wan-priority", label: "WAN Priority", icon: FiList, modes: ["direct"] },
      { key: "qos", label: "QoS Policies", icon: FiShield },
      { key: "cloud", label: "Cloud Gateway", icon: FiCloud, modes: ["anchored"] },
      { key: "device-limits", label: "Device Speed Limits", icon: FiUsers },
    ],
  },
  {
    // Where Flows Exit observes the anchor dispersion Obfuscation configures.
    // Agent Rules is each organisation's, the same whichever box is picked.
    id: "security",
    label: "Security",
    items: [
      { key: "firewall", label: "Firewall", icon: FiShield },
      { key: "encryption", label: "Encryption", icon: FiLock, modes: ["anchored"] },
      { key: "obfuscation", label: "Obfuscation", icon: FiEyeOff, modes: ["anchored"] },
      { key: "flow-exits", label: "Where Flows Exit", icon: FiGitBranch, modes: ["anchored"] },
      { key: "agent-rules", label: "Agent Rules", icon: FiFileText, hideOnSingle: true, phone: true },
    ],
  },
  {
    id: "troubleshooting",
    label: "Troubleshooting",
    items: [
      { key: "diag-overview", label: "System Overview", icon: FiCpu },
      { key: "diag-wan", label: "WAN Deep Dive", icon: FiRadio, phone: true },
      { key: "wifi", label: "Wi-Fi Networks", icon: FiWifi },
      { key: "as-path", label: "AS Path", icon: FiShare2, modes: ["anchored"] },
      { key: "remote-access", label: "Remote Access", icon: FiMonitor, modes: ["anchored"] },
    ],
  },
  {
    // Live readouts and specialist tools most visits never need. The fleet's
    // two keep the fleet group's gates: portal only, and offered with a phone
    // selected.
    id: "advanced",
    label: "Advanced",
    items: [
      { key: "interface-traffic", label: "Live Traffic", icon: FiBarChart2, phone: true },
      { key: "diag-devices", label: "Devices & Flows", icon: FiTerminal },
      { key: "events", label: "Activity Log", icon: FiClock, hideOnSingle: true, phone: true },
      { key: "deep-telemetry", label: "Deep Telemetry", icon: FiCpu, hideOnSingle: true, phone: true },
      { key: "power", label: "Power & Cost", icon: FiBatteryCharging },
      { key: "aqm", label: "AQM / Bufferbloat", icon: FiWind, modes: ["anchored"], hideOnSingle: true },
      { key: "seqforecast", label: "AI Prediction (Trial)", icon: FiCpu, modes: ["anchored"] },
      { key: "tak", label: "TAK Map Marker", icon: FiMapPin, modes: ["anchored"] },
    ],
  },
  {
    // Engineering tools that load the links or collect training data.
    id: "lab",
    label: "Lab",
    hideOnSingle: true,
    items: [
      { key: "stress-test", label: "Stress Tester", icon: FiZap, modes: ["anchored"] },
      { key: "dataset", label: "Dataset Collection", icon: FiDatabase, modes: ["anchored"] },
    ],
  },
  {
    id: "admin",
    label: "Administration",
    hideOnSingle: true,
    phone: true,
    // Anyone signed in may sign up for alerts and see where updates stand;
    // accounts are for admins.
    items: [
      { key: "access", label: "Access Control", icon: FiShield, minRole: "tenant_admin" },
      { key: "alerts", label: "Alerts", icon: FiBell },
      { key: "updates", label: "Software Updates", icon: FiDownloadCloud },
      { key: "assistant", label: "Assistant", icon: FiCpu, minRole: "tenant_admin" },
      { key: "outside-agents", label: "Outside Agents", icon: FiKey, minRole: "tenant_admin" },
      // For everyone signed in: an approver may hold any role.
      { key: "approvers", label: "Approvers", icon: FiCheckCircle },
      { key: "threat-intel", label: "Threat Intelligence", icon: FiCrosshair, minRole: "super_admin" },
    ],
  },
];

const offered = (category, item, { mode, singleEdge, phone } = {}) =>
  (!item.modes || item.modes.includes(mode || "anchored"))
  && !(singleEdge && (category.hideOnSingle || item.hideOnSingle))
  && !(phone && !(category.phone || item.phone))
  && !(item.phoneOnly && !phone);

function locate(view) {
  for (const category of NAV_CATEGORIES) {
    const item = category.items.find((it) => it.key === view);
    if (item) return { category, item };
  }
  return null;
}

/** navFor is the navigation one edge offers on this build. A group left with
 *  nothing in it is dropped rather than drawn as a bare header. */
export function navFor(context) {
  return NAV_CATEGORIES
    .map((category) => ({ ...category, items: category.items.filter((item) => offered(category, item, context)) }))
    .filter((category) => category.items.length > 0);
}

/** viewOffered says whether a screen exists for this edge on this build, so a
 *  deep link or an edge switch never strands the operator on one the sidebar
 *  does not show. A view the navigation does not list is not gated here. */
export function viewOffered(view, context) {
  const found = locate(view);
  return !found || offered(found.category, found.item, context);
}

/** categoryOf is the group a screen belongs to. */
export const categoryOf = (view) => locate(view)?.category.id || null;

/** navLabel is the name the sidebar gives a screen, for anything that sends the
 *  operator there. */
export const navLabel = (view) => locate(view)?.item.label;

/** navIcon is the icon the sidebar gives a screen, so the phone's bar draws a
 *  place as the sidebar does. */
export const navIcon = (view) => locate(view)?.item.icon;
